import React from 'react';
import { Box, Typography, styled } from '@mui/material';
import { numberToCurrencyString, pluralize } from 'further-ui/utils';
import { LinkedCapitalAction } from 'further-types/lp/capital-action';
import { sortAllocations } from '../utils/sortAllocations';
import TabsPanel from 'further-ui/components/TabsPanel';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { Column } from 'further-ui/components/Layout';
import AvatarWithName from 'components/new/AvatarWithName';

const ContentColumn = styled(Column)(({ theme }) => ({
  padding: theme.spacing(
    0,
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
  ),
}));

export const CapitalActionName: React.FC<{
  name: string;
  capitalActionHeader: {
    investments: number;
    expenses: number;
    capitalRefunds: number;
    fees: number;
    additionalPayments: number;
  };
}> = ({ name, capitalActionHeader }) => {
  const items = [
    { count: capitalActionHeader?.investments, label: 'investment' },
    { count: capitalActionHeader?.expenses, label: 'expense' },
    { count: capitalActionHeader?.capitalRefunds, label: 'capital refund' },
    { count: capitalActionHeader?.fees, label: 'fee' },
    {
      count: capitalActionHeader?.additionalPayments,
      label: 'additional payment',
    },
  ];

  const subtitle = items
    .filter((item) => (item.count ?? 0) > 0)
    .map((item) => `${item.count} ${pluralize(item.label, item.count)}`)
    .join(', ');

  return (
    <Box>
      <Typography variant="body1">{name ?? ''}</Typography>
      <Typography variant="body1">{subtitle}</Typography>
    </Box>
  );
};

interface TransferLinkedCapitalActionCardProps {
  capitalAction: LinkedCapitalAction;
  baseCurrency: string;
}

const TransferLinkedCapitalActionCard: React.FC<
  TransferLinkedCapitalActionCardProps
> = ({ capitalAction, baseCurrency }) => {
  const sortedOriginalBreakdown = sortAllocations(
    capitalAction.originalInvestorBreakdown,
  );
  const sortedNewBreakdown = sortAllocations(
    capitalAction.newInvestorBreakdown,
  );

  const originalRows = sortedOriginalBreakdown.map((row, idx) => ({
    id: `original-${idx}`,
    ...row,
  }));

  const newRows = sortedNewBreakdown.map((row, idx) => ({
    id: `new-${idx}`,
    ...row,
  }));

  type OriginalRow = (typeof originalRows)[number];
  type NewRow = (typeof newRows)[number];

  const sharedColumns = <T extends OriginalRow>(): Array<TableColumn<T>> => [
    {
      key: 'investorName',
      label: 'Name',
      render: (row) => <AvatarWithName name={row.investorName} />,
    },
    {
      key: 'investments',
      label: 'Call - Investments',
      render: (row) =>
        numberToCurrencyString(row.investments, {
          fallback: '-',
          currency: baseCurrency,
        }),
    },
    {
      key: 'expenses',
      label: 'Call - Expenses',
      render: (row) =>
        numberToCurrencyString(row.expenses, {
          fallback: '-',
          currency: baseCurrency,
        }),
    },
    {
      key: 'fees',
      label: 'Call - Fees',
      render: (row) =>
        numberToCurrencyString(row.fees, {
          fallback: '-',
          currency: baseCurrency,
        }),
    },
    {
      key: 'returnOfCapital',
      label: 'Capital refunds',
      render: (row) =>
        numberToCurrencyString(row.returnOfCapital, {
          fallback: '-',
          currency: baseCurrency,
        }),
    },
    {
      key: 'netCallOrDistribution',
      label: 'Net call/(distribution)',
      render: (row) =>
        numberToCurrencyString(row.netCallOrDistribution, {
          fallback: '-',
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
  ];

  const newColumns: Array<TableColumn<NewRow>> = [
    ...sharedColumns<NewRow>(),
    {
      key: 'callOverpaidOrUnderpaid',
      label: 'Call underpaid/(overpaid)',
      render: (row) =>
        numberToCurrencyString(row.callOverpaidOrUnderpaid, {
          fallback: '-',
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
  ];

  return (
    <TabsPanel
      defaultTab="after"
      tabs={[
        {
          key: 'after',
          label: 'New allocation',
          content: (
            <ContentColumn>
              <Table
                showTopBorder
                showHeaderBorder
                columns={newColumns}
                rows={newRows}
                scrollable={true}
              />
            </ContentColumn>
          ),
        },
        {
          key: 'before',
          label: 'Original allocation',
          content: (
            <ContentColumn>
              <Table
                showTopBorder
                showHeaderBorder
                columns={sharedColumns()}
                rows={originalRows}
                scrollable={true}
              />
            </ContentColumn>
          ),
        },
      ]}
    />
  );
};

export default TransferLinkedCapitalActionCard;
